import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, AlertTriangle, Lightbulb, TrendingUp, RefreshCw, ChevronDown, ChevronUp, Camera, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { db } from "@/api/db";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

const BASE_STEPS = [
  { label: "Carregando transcrição da entrevista",    detail: "Lendo todas as perguntas e respostas" },
  { label: "Avaliando respostas e argumentação",       detail: "Analisando clareza, profundidade e estrutura" },
  { label: "Calculando scorecard de competências",     detail: "Pontuando comunicação, segurança e fit cultural" },
  { label: "Identificando pontos de melhoria",         detail: "Mapeando o que te prejudicou e como corrigir" },
  { label: "Gerando plano de desenvolvimento",         detail: "Criando ações práticas para os próximos 7 dias" },
  { label: "Finalizando relatório completo",            detail: "Compilando todos os resultados" },
];
const VIDEO_STEP = { label: "Processando análise visual",  detail: "Avaliando cenário, postura, iluminação e vestimenta" };

function AnalysisLoadingScreen({ hasVideo }) {
  const steps = hasVideo
    ? [...BASE_STEPS.slice(0, -1), VIDEO_STEP, BASE_STEPS[BASE_STEPS.length - 1]]
    : BASE_STEPS;

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(s => Math.min(s + 1, steps.length - 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="max-w-lg mx-auto py-12 space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "#EAFAF6" }}>
          <motion.div
            className="w-7 h-7 rounded-full border-[3px]"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }}
            animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
        </div>
        <h3 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Analisando sua entrevista…
        </h3>
        <p className="text-sm" style={{ color: "#646C6F" }}>
          A IA está revisando cada detalhe. Leva cerca de 30 segundos.
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, i) => {
          const done    = i < currentStep;
          const active  = i === currentStep;
          const pending = i > currentStep;
          return (
            <motion.div key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: pending ? 0.35 : 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="flex items-start gap-3 px-4 py-3 rounded-2xl"
              style={{
                background: done ? "#EAFAF6" : active ? "#F9FAFA" : "transparent",
                border: active ? "1.5px solid #00DCA4" : "1.5px solid transparent",
              }}>
              {/* Icon */}
              <div className="mt-0.5 flex-shrink-0">
                {done  && <CheckCircle2 className="w-4 h-4" style={{ color: "#00DCA4" }} />}
                {active && (
                  <motion.div className="w-4 h-4 rounded-full border-2"
                    style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }}
                    animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} />
                )}
                {pending && <Circle className="w-4 h-4" style={{ color: "#C8CED0" }} />}
              </div>
              {/* Text */}
              <div>
                <p className="text-sm font-semibold leading-tight"
                  style={{ color: done ? "#00664C" : active ? "#003326" : "#99A6AB" }}>
                  {step.label}
                </p>
                {(done || active) && (
                  <p className="text-xs mt-0.5" style={{ color: done ? "#00664C" : "#646C6F" }}>
                    {step.detail}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 70 ? "#00DCA4" : score >= 50 ? "#FF9D66" : "#892E38";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full" style={{ background: "#E5E9EA" }}>
        <motion.div className="h-full rounded-full" style={{ background: color, width: `${score}%` }}
          initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.8 }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{score}</span>
    </div>
  );
}

export default function InterviewAnalysis({ session, onRestart }) {
  const [analysis, setAnalysis] = useState(session.analysis || null);
  const [loading, setLoading] = useState(!session.analysis);
  const [errorMsg, setErrorMsg] = useState(null);
  const [openSection, setOpenSection] = useState("scorecard");

  useEffect(() => {
    if (!analysis) loadAnalysis();
  }, []);

  async function loadAnalysis() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const safeTranscript = Array.isArray(session.transcript) ? session.transcript : [];
      const safeFrames = Array.isArray(session.video_frames) ? session.video_frames : [];
      const res = await db.functions.invoke("interviewAnalysis", {
        session_id: session.id,
        transcript: safeTranscript,
        video_frames: safeFrames,
      });
      const payload = res?.data ?? res;
      if (payload?.error) throw new Error(payload.error);
      const data = payload?.analysis;
      if (!data) throw new Error("Resposta inválida da IA — tente novamente.");
      setAnalysis(data);
      db.entities.InterviewSession.update(session.id, { analysis: data, status: "done" })
        .catch(err => console.warn("[DB save analysis]", err));
    } catch (err) {
      console.error("Erro na análise:", err);
      setErrorMsg(err?.message || "Erro ao gerar análise. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <AnalysisLoadingScreen hasVideo={(session.video_frames || []).length > 0} />;
  }

  if (errorMsg) {
    return (
      <div className="max-w-xl mx-auto text-center space-y-4 py-16">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "#FADBDF" }}>
          <AlertTriangle className="w-8 h-8" style={{ color: "#892E38" }} />
        </div>
        <h3 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Falha na análise</h3>
        <p className="text-sm" style={{ color: "#646C6F" }}>{errorMsg}</p>
        <button onClick={loadAnalysis}
          className="px-6 py-3 rounded-full text-sm font-bold"
          style={{ background: "#00DCA4", color: "#003326" }}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!analysis) return null;

  const overall = analysis.overall_score || 0;
  const overallColor = overall >= 70 ? "#00DCA4" : overall >= 50 ? "#FF9D66" : "#892E38";

  const sections = [
    { key: "scorecard", label: "Scorecard por competência", icon: Trophy },
    { key: "weaknesses", label: "O que te prejudicou", icon: AlertTriangle },
    { key: "rewrites", label: "Como deveria ter respondido", icon: Lightbulb },
    { key: "plan", label: "Plano de melhoria (7 dias)", icon: TrendingUp },
    { key: "visual", label: "Análise Visual", icon: Camera },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Overall */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 text-center" style={{ background: "linear-gradient(135deg, #003326, #00664C)" }}>
        <p className="text-xs font-semibold mb-2" style={{ color: "#61E8C6" }}>RESULTADO FINAL</p>
        <div className="mb-1 flex items-baseline justify-center gap-1">
          <span className="text-6xl font-black" style={{ fontFamily: "Poppins, sans-serif", color: overallColor }}>{overall}</span>
          <span className="text-lg font-bold" style={{ color: "rgba(176,252,234,0.5)" }}>/100</span>
        </div>
        <p className="text-sm" style={{ color: "#B0FCEA" }}>{analysis.overall_feedback || "Análise concluída"}</p>
      </motion.div>

      {/* Sections */}
      {sections.map(sec => {
        const isOpen = openSection === sec.key;
        return (
          <div key={sec.key} className="rounded-2xl overflow-hidden bg-white" style={{ border: "1px solid #E5E9EA" }}>
            <button onClick={() => setOpenSection(isOpen ? null : sec.key)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left">
              <sec.icon className="w-4 h-4 flex-shrink-0" style={{ color: "#00DCA4" }} />
              <span className="text-sm font-bold flex-1" style={{ color: "#003326" }}>{sec.label}</span>
              {isOpen ? <ChevronUp className="w-4 h-4" style={{ color: "#99A6AB" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "#99A6AB" }} />}
            </button>

            {isOpen && (
              <div className="px-5 pb-5">
                {/* SCORECARD */}
                {sec.key === "scorecard" && (
                  <div className="space-y-3">
                    {(analysis.scorecard || []).map((item, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold" style={{ color: "#2E3233" }}>{item.dimension}</span>
                        </div>
                        <ScoreBar score={item.score} />
                        <p className="text-xs mt-1" style={{ color: "#646C6F" }}>{item.comment}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* WEAKNESSES */}
                {sec.key === "weaknesses" && (
                  <div className="space-y-3">
                    {(analysis.weaknesses || []).map((w, i) => (
                      <div key={i} className="flex gap-3 p-3 rounded-xl" style={{ background: "#FADBDF" }}>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#892E38" }} />
                        <p className="text-sm" style={{ color: "#892E38" }}>{w}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* REWRITES */}
                {sec.key === "rewrites" && (
                  <div className="space-y-5">
                    {(analysis.rewrites || []).map((r, i) => (
                      <div key={i} className="space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: "#99A6AB" }}>Pergunta {i + 1}</p>
                        <p className="text-sm font-semibold" style={{ color: "#003326" }}>{r.question}</p>
                        <div className="rounded-xl p-3" style={{ background: "#FADBDF" }}>
                          <p className="text-xs font-bold mb-1" style={{ color: "#892E38" }}>❌ O que você disse</p>
                          <p className="text-sm" style={{ color: "#892E38" }}>{r.user_answer}</p>
                          <p className="text-xs mt-2" style={{ color: "#65541B" }}>{r.why_weak}</p>
                        </div>
                        <div className="rounded-xl p-3" style={{ background: "#EAFAF6" }}>
                          <p className="text-xs font-bold mb-1" style={{ color: "#00664C" }}>✓ Como deveria ter respondido</p>
                          <p className="text-sm" style={{ color: "#003326" }}>{r.model_answer}</p>
                          {r.framework && (
                            <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "#B0FCEA", color: "#00664C" }}>
                              Framework: {r.framework}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* PLAN */}
                {sec.key === "plan" && (
                  <div className="space-y-3">
                    {(analysis.improvement_plan || []).map((item, i) => (
                      <div key={i} className="flex gap-3 p-3 rounded-xl" style={{ background: "#F2F4F4" }}>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                          style={{ background: "#00DCA4", color: "#003326" }}>{i + 1}</div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: "#003326" }}>{item.action}</p>
                          {item.how && <p className="text-xs mt-0.5" style={{ color: "#646C6F" }}>{item.how}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* VISUAL */}
                {sec.key === "visual" && (
                  <div className="space-y-3">
                    {analysis.visual_analysis ? (
                      <>
                        <p className="text-sm mb-3" style={{ color: "#646C6F" }}>{analysis.visual_analysis.visual_summary}</p>
                        {(analysis.visual_analysis.dimensions || []).map((d, i) => (
                          <div key={i}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold" style={{ color: "#2E3233" }}>{d.label}</span>
                            </div>
                            <ScoreBar score={d.score} />
                            <p className="text-xs mt-1" style={{ color: "#646C6F" }}>{d.feedback}</p>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="text-center py-6 space-y-2">
                        <Camera className="w-8 h-8 mx-auto" style={{ color: "#C8CED0" }} />
                        <p className="text-sm" style={{ color: "#99A6AB" }}>
                          Câmera não foi autorizada durante a entrevista.<br />
                          Permita o acesso à câmera na próxima simulação para receber análise visual.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Actions */}
      <div className="flex gap-3 pb-8">
        <button onClick={onRestart}
          className="flex-1 py-3.5 rounded-full text-sm font-bold flex items-center justify-center gap-2"
          style={{ background: "#00DCA4", color: "#003326" }}>
          <RefreshCw className="w-4 h-4" /> Nova tentativa
        </button>
        <Link to={createPageUrl("Home")} className="flex-1 py-3.5 rounded-full text-sm font-bold flex items-center justify-center"
          style={{ background: "#F2F4F4", color: "#646C6F" }}>
          Voltar ao início
        </Link>
      </div>
    </div>
  );
}